import React from 'react';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import Navbar from 'components/PartnerPage/Navbar';
import HeroSectionWithQuickQuote from 'components/PartnerPage/HeroWithQuickQuote'
import Footer from 'components/PartnerPage/Footer';
import ShipOverview from 'components/PartnerPage/ShipOverview';
import FeaturesGrid from 'components/PartnerPage/FeaturesGrid';
import Newsletter from 'components/PartnerPage/Newsletter';
import ShipAndPlayPromise from 'components/PartnerPage/ShipPlayPromise';
import backgroundImageMobile from 'images/shipplay/backgrounds/mobile/inspirato-hero-background.jpg';
import backgroundImageDesktop from 'images/shipplay/backgrounds/desktop/inspirato-hero-background.jpg';
import NavLogo from 'images/shipplay/logos/inspirato-nav-logo.png';
import FooterLogo from 'images/shipplay/logos/inspirato-footer-logo.png';
import ShipPlayLogo from 'images/shipplay/logos/shipplay-logo.png';

const backgroundImageStyles = {
  '--background-image-mobile': `url(${backgroundImageMobile})`,
  '--background-image-desktop': `url(${backgroundImageDesktop})`
}

const imageModules = import.meta.glob('images/shipplay/inspirato/*.jpg', { eager: true });

const getImageUrl = (imageName) => {
  const imagePath = `/images/shipplay/inspirato/${imageName}.jpg`;
  const image = imageModules[`${imagePath}`];
  return image?.default;
};

const generateCTAPath = (couponCode) => {
  return couponCode ? '/ship?coupon=ispo24' : '/pricing';
};

function InspiratoPage({ couponCode, quickQuoteTitle, termsConditionsText }) {
  return (
    <>
      <ToastContainer />
      {/* Background image container with responsive height */}
      <div style={backgroundImageStyles} className="relative font-poppins bg-cover bg-center backgroundImage">
        <Navbar NavLogo={NavLogo}
                subdomain="inspirato"
                customCTAPath={generateCTAPath(couponCode)}
                showExtraLinks={true}
        />
        <HeroSectionWithQuickQuote
          header="Shipping luggage offers luxury, ease, and convenience, even before you check-in."
          subheader="
            Introducing Ship&Play, an easy and reliable option for
            shipping items to and from your destination
          "
          quickQuoteTitle={quickQuoteTitle}
          breakText="Rates starting at $75"
          buttonText="Get a Quote and Apply Discount"
          couponCode={couponCode}
        />

      </div>

      <main className="flex flex-col items-center justify-between w-full font-poppins">
        <ShipOverview
          buttonCTAPath={generateCTAPath(couponCode)}
          reasons={[{
            title: 'Why Ship?',
            description: `
              Ensure your vacation starts and ends on the right note, allowing you
              to travel light and enjoy every moment without the burden of luggage.
            `,
          }, {
            title: 'How it Works',
            description: `
              Use the box above to insert where you are shipping from and where you
              are going. Choose how fast you need it there and book your order. We'll
              send you a label and supplies for attaching it to your bag. With your
              pick-up scheduled, we'll do the rest.
            `,
          }, {
            title: 'What to Expect',
            description: `
              Like you may have experienced with Ship Sticks or Ship Skis, expect the
              same level of reliability, dedicated customer service, and detailed
              tracking with Ship&Play. We ensure your items arrive intact and on
              time, every time.
            `,
          },
        ]}>
          <p className="text-[28px] lg:text-[32px] lg:leading-[58px] font-bold text-[#1C2938] mb-5">
            About <img src={ShipPlayLogo} alt="Ship & Play Logo" className="inline-block w-[166px] ml-2" />
          </p>
          <p className="text-[#231F20] text-lg leading-[30px]">
            Discover a new way of traveling with Ship&Play, the premier shipping service
            for luggage, sports equipment and other items needed for your trip.
          </p>
          <p className="text-[#231F20] text-lg leading-[30px] mt-7">
            The company behind Ship Sticks and Ship Skis brings over a decade of expertise,
            directly to your door, ensuring a stress-free journey from your home to any
            Inspirato property.
          </p>
          <p className="text-[#231F20] text-lg leading-[30px] mt-7">
            Whether it&apos;s toys for the kids, that indispensable set up clubs, or a dress
            you can&apos;t afford to lose, Ship&Play guarantees your essentials travel safely
            and arrive ready for your adventure.
          </p>
        </ShipOverview>
        <FeaturesGrid
          header="When to think about Ship&Play"
          features={[
            {
              image: getImageUrl('international-travel'),
              title: 'International Travel',
              description: `
                Navigating customs and worrying over lengthy baggage delays at international hubs is
                no way to start a trip. We help bypass customs lines and avoid transfer mishaps.
              `
            },
            {
              image: getImageUrl('multi-destination'),
              title: 'Multi-Destination',
              description: `
                Some trips can take you to low and high, literally! If you're in the mountains for the first
                half, then plan to hit the beach, send those ski clothes home and the beach clothes ahead.
              `
            },
            {
              image: getImageUrl('golf-ski-and-adventure'),
              title: 'Golf, Ski and Adventure',
              description: `
                Every adventure has unique equipment you can't go without. From golf clubs and skis, to
                gear for diving and camping. When it has to be your own, pack it up and send it ahead.
              `
            },
            {
              image: getImageUrl('traveling-with-young-kids'),
              title: 'Traveling with Young Kids',
              description: `
                Little ones bring a lot of joy, but they also need a lot of stuff! Ship the toys, snacks,
                and Pack 'n Play so they feel right at home.
              `
            },
            {
              image: getImageUrl('special-events'),
              title: 'Special Events',
              description: `
                50th birthday party in Cabo? Wedding in Tuscany? Destination events are amazing, but the
                locals may not have everything you need to pull off an unforgettable celebration.
              `
            },
            {
              image: getImageUrl('cruises-and-connections'),
              title: 'Cruises and Connections',
              description: `
                When timing is everything, you can't afford to have luggage slow you down. Cruise ships
                and other connections aren't waiting for your bags, so when you can't risk it, ship it.
              `
            }
        ]} />
        <ShipAndPlayPromise buttonCTAPath={generateCTAPath(couponCode)} />
      </main>
      <Footer
        FooterLogo={FooterLogo} logoSize="w-[327px] lg:w-[497px]" brand="shipplay"
        shipLinkPath={generateCTAPath(couponCode)} subdomain="inspirato"
      >
        {/* T&C */}
        <div className="text-left pb-8 lg:py-8 pl-[5%] lg:pl-0 gap-12">
          <div className="flex items-center justify-between">
            <h5 className="text-white text-[22px] font-semibold mb-2">Deal Terms & Conditions</h5>
            <span className="hidden lg:block h-[3px] w-4 bg-green-500"></span>
          </div>
          <p className="my-2 text-lg leading-8">
            {termsConditionsText}
          </p>
        </div>
        <Newsletter />
      </Footer>
    </>
  );
}

InspiratoPage.propTypes = {
  couponCode: PropTypes.string,
  quickQuoteTitle: PropTypes.string,
  termsConditionsText: PropTypes.string
};

export default InspiratoPage;
